import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";

import {
  removeMiscAnalysisFromList,
  setCurrentMiscAnalysis,
  setEditingMiscAnalysis,
  setIsCurrentLoading,
  setIsListInitialized,
  setListData,
  setShowCreatePanel,
  setShowEditPanel,
  updateMiscAnalysisInList,
} from "../miscAnalysisSlice";

const API_BASE = "/api/nextgen/misc-analysis";

export const fetchMiscAnalysisList =
  (sortColumn = "created_date", sortOrder = "DESC", searchQuery = "") =>
  async (dispatch) => {
    dispatch(addToJobQueue("fetchMiscAnalysis"));

    try {
      const params = new URLSearchParams({
        sortColumn,
        sortOrder,
      });
      if (searchQuery) {
        params.append("search", searchQuery);
      }
      const { data } = await localhost.get(`${API_BASE}/list?${params}`);
      dispatch(setListData(data || []));
      dispatch(setIsListInitialized(true));
      return data;
    } catch (error) {
      dispatch(showErrorModal(error));
      return [];
    } finally {
      dispatch(removeFromJobQueue("fetchMiscAnalysis"));
    }
  };

export const fetchMiscAnalysisById = (id) => async (dispatch) => {
  dispatch(setIsCurrentLoading(true));
  try {
    const { data } = await localhost.get(`${API_BASE}/${id}`);
    dispatch(setCurrentMiscAnalysis(data));
    return data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return { notFound: true };
    }
    dispatch(showErrorModal(error));
    return false;
  } finally {
    dispatch(setIsCurrentLoading(false));
  }
};

export const createMiscAnalysis =
  (miscAnalysisData, onSuccess) => async (dispatch, getState) => {
    try {
      const { data } = await localhost.post(API_BASE, miscAnalysisData);
      const { sortColumn, sortOrder, searchQuery } = getState().miscAnalysis;
      await dispatch(fetchMiscAnalysisList(sortColumn, sortOrder, searchQuery));
      dispatch(setShowCreatePanel(false));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `${data.name} created successfully`,
        })
      );
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      dispatch(showErrorModal(error));
    }
  };

export const updateMiscAnalysis =
  (id, miscAnalysisData, onSuccess) => async (dispatch) => {
    try {
      const { data } = await localhost.put(
        `${API_BASE}/${id}`,
        miscAnalysisData
      );
      dispatch(updateMiscAnalysisInList(data));
      dispatch(setCurrentMiscAnalysis(data));
      dispatch(setShowEditPanel(false));
      dispatch(setEditingMiscAnalysis(null));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `${data.name} updated successfully`,
        })
      );
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      dispatch(showErrorModal(error));
    }
  };

export const deleteMiscAnalysis = (id, name, onSuccess) => async (dispatch) => {
  try {
    await localhost.delete(`${API_BASE}/${id}`);
    dispatch(removeMiscAnalysisFromList(id));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: `${name} deleted successfully`,
      })
    );
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    dispatch(showErrorModal(error));
  }
};

export const linkDocument =
  (miscAnalysisId, documentId, onSuccess) => async (dispatch) => {
    try {
      await localhost.post(`${API_BASE}/${miscAnalysisId}/documents`, null, {
        params: { documentId },
      });
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Document linked successfully",
        })
      );
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      dispatch(showErrorModal(error));
    }
  };

export const unlinkDocument =
  (miscAnalysisId, documentId, onSuccess) => async (dispatch) => {
    try {
      await localhost.delete(
        `${API_BASE}/${miscAnalysisId}/documents/${documentId}`
      );
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Document unlinked successfully",
        })
      );
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      dispatch(showErrorModal(error));
    }
  };

export const fetchArchivedMiscAnalysisList = () => async (dispatch) => {
  dispatch(addToJobQueue("fetchArchivedMiscAnalysis"));
  dispatch(setListData([]));

  try {
    const { data } = await localhost.get(`${API_BASE}/archived`);
    dispatch(setListData(data || []));
    dispatch(setIsListInitialized(true));
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("fetchArchivedMiscAnalysis"));
  }
};

export const unarchiveMiscAnalysis = (id) => async (dispatch) => {
  try {
    await localhost.post(`${API_BASE}/${id}/unarchive`);
    dispatch(removeMiscAnalysisFromList(id));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Analysis unarchived successfully",
      })
    );
  } catch (error) {
    dispatch(showErrorModal(error));
  }
};

export const deleteMiscAnalysisById = (id, name) => async (dispatch) => {
  try {
    await localhost.delete(`${API_BASE}/${id}`);
    dispatch(removeMiscAnalysisFromList(id));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: `${name} deleted successfully`,
      })
    );
  } catch (error) {
    dispatch(showErrorModal(error));
  }
};

export const editMiscAnalysisArchiveReason = ({ id, archiveStatusId, archiveNote }) => async (dispatch) => {
  try {
    await localhost.put(`${API_BASE}/${id}/archive-reason`, { archiveStatusId, archiveNote });
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Archive reason updated successfully",
      })
    );
    await dispatch(fetchMiscAnalysisById(id));
  } catch (error) {
    dispatch(showErrorModal(error));
    throw error;
  }
};

export const fetchDocumentIds = (miscAnalysisId) => async (dispatch) => {
  try {
    const { data } = await localhost.get(
      `${API_BASE}/${miscAnalysisId}/documents`
    );
    return data;
  } catch (error) {
    dispatch(showErrorModal(error));
    return [];
  }
};

export const archiveMiscAnalysis = (id, name, { archiveStatusId, archiveNote } = {}) => async (dispatch) => {
  try {
    await localhost.post(`${API_BASE}/${id}/archive`, { archiveStatusId, archiveNote });
    dispatch(removeMiscAnalysisFromList(id));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: `${name} archived successfully`,
      })
    );
  } catch (error) {
    if (
      error?.response?.status === 423 &&
      error?.response?.data?.lockedItems
    ) {
      dispatch(
        showModal(modalTypes.LOCKED_ITEMS_MODAL, {
          lockedItems: error.response.data.lockedItems,
          handleOk: hideModal(modalTypes.LOCKED_ITEMS_MODAL),
        })
      );
    } else {
      dispatch(showErrorModal(error));
    }
  }
};
